import {
  Briefcase,
  Calendar,
  Check,
  ChevronsUpDown,
  Inbox,
  KanbanSquare,
  LayoutDashboard,
  LogOut,
  Menu,
  Settings,
  ShieldCheck,
  Sparkles,
  User as UserIcon,
  Users,
} from "lucide-react"
import * as React from "react"
import { Link, NavLink, Outlet, useLocation } from "react-router-dom"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Sheet, SheetContent } from "@/components/ui/sheet"
import { useAuth } from "@/hooks/use-auth"
import { useMediaQuery } from "@/hooks/use-media-query"
import { cn, initials } from "@/lib/utils"

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Genel bakış", end: true },
  { to: "/dashboard/jobs", icon: Briefcase, label: "İlanlar" },
  { to: "/dashboard/candidates", icon: Users, label: "Adaylar" },
  { to: "/dashboard/pipeline", icon: KanbanSquare, label: "Pipeline" },
  { to: "/dashboard/inbox", icon: Inbox, label: "Gelen kutusu" },
  { to: "/dashboard/interviews", icon: Calendar, label: "Mülakatlar" },
  { to: "/dashboard/settings", icon: Settings, label: "Ayarlar" },
]

export function DashboardLayout() {
  const isDesktop = useMediaQuery("(min-width: 1024px)")
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const location = useLocation()

  React.useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-muted/30 lg:grid lg:grid-cols-[256px_minmax(0,1fr)]">
      {isDesktop ? (
        <aside className="hidden lg:flex lg:flex-col lg:border-r lg:bg-background">
          <SidebarContent />
        </aside>
      ) : (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="w-72 p-0">
            <SidebarContent />
          </SheetContent>
        </Sheet>
      )}

      <main className="flex min-w-0 flex-col">
        {!isDesktop && (
          <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Menüyü aç"
              onClick={() => setMobileOpen(true)}
            >
              <Menu className="size-5" />
            </Button>
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-base font-semibold tracking-tight"
            >
              <span className="grid size-6 place-items-center rounded-md bg-primary text-primary-foreground">
                <Sparkles className="size-3.5" aria-hidden="true" />
              </span>
              Hirefy
            </Link>
            <UserMenu />
          </header>
        )}
        <Outlet />
      </main>
    </div>
  )
}

function SidebarContent() {
  const { user, memberships, activeOrg, switchOrg, signOut } = useAuth()
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center px-6">
        <Link
          to="/dashboard"
          className="flex items-center gap-2 text-lg font-semibold tracking-tight"
        >
          <span className="grid size-7 place-items-center rounded-md bg-primary text-primary-foreground shadow-sm">
            <Sparkles className="size-4" aria-hidden="true" />
          </span>
          Hirefy
        </Link>
      </div>
      <div className="border-t" />
      {memberships.length > 0 && (
        <div className="p-3">
          <OrgSwitcher
            memberships={memberships}
            activeOrg={activeOrg}
            onSwitch={switchOrg}
          />
        </div>
      )}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3 pt-0">
        {NAV.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                isActive
                  ? "bg-primary-soft text-primary"
                  : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
              )
            }
          >
            <Icon className="size-4" />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t" />
      <div className="space-y-3 p-3">
        {user?.is_superuser && (
          <Link
            to="/admin"
            className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
          >
            <ShieldCheck className="size-3.5" />
            Sistem ayarları
          </Link>
        )}
        <div className="flex items-center gap-2">
          <Avatar className="size-8">
            <AvatarFallback>{initials(user?.full_name ?? user?.email)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">
              {user?.full_name ?? user?.email}
            </div>
            <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
          </div>
          <Button size="icon" variant="ghost" aria-label="Çıkış" onClick={() => signOut()}>
            <LogOut className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function OrgSwitcher({
  memberships,
  activeOrg,
  onSwitch,
}: {
  memberships: ReturnType<typeof useAuth>["memberships"]
  activeOrg: ReturnType<typeof useAuth>["activeOrg"]
  onSwitch: (id: string) => Promise<void>
}) {
  if (memberships.length === 1 && activeOrg) {
    return (
      <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
        <div className="text-muted-foreground">Aktif organizasyon</div>
        <div className="truncate font-medium">{activeOrg.organization.name}</div>
        <div className="text-muted-foreground">Plan: {activeOrg.organization.plan}</div>
      </div>
    )
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 rounded-md border bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-muted/40"
        >
          <div className="min-w-0">
            <div className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">
              Organizasyon
            </div>
            <div className="truncate font-medium">
              {activeOrg?.organization.name ?? "Seç"}
            </div>
          </div>
          <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Organizasyonlar</DropdownMenuLabel>
        {memberships.map((m) => (
          <DropdownMenuItem
            key={m.organization_id}
            onSelect={() => {
              if (m.organization_id !== activeOrg?.organization_id) {
                void onSwitch(m.organization_id)
              }
            }}
          >
            <div className="flex-1 min-w-0">
              <div className="truncate text-sm font-medium">{m.organization.name}</div>
              <div className="truncate text-xs text-muted-foreground">
                {m.role} · {m.organization.plan}
              </div>
            </div>
            {m.organization_id === activeOrg?.organization_id && (
              <Check className="size-4 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function UserMenu() {
  const { user, signOut } = useAuth()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" aria-label="Hesap menüsü" className="outline-none">
          <Avatar className="size-8">
            <AvatarFallback>{initials(user?.full_name ?? user?.email)}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="normal-case">
          <div className="font-medium normal-case tracking-normal">
            {user?.full_name ?? "Hesap"}
          </div>
          <div className="text-xs font-normal normal-case tracking-normal text-muted-foreground">
            {user?.email}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/dashboard/settings">
            <UserIcon className="size-4" /> Profil & ayarlar
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem destructive onSelect={() => void signOut()}>
          <LogOut className="size-4" /> Çıkış yap
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
